/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */

import {
  IAgentPlugin,
  IPluginMethodMap,
} from "@vlinder-web/plugin-agent-service-react";
import { GraphqlServerModule } from "@vlinder-web/graphql-server-module-react";
import { VApplication } from "../../../application";
import { CREATE_ORDER } from "../graphql/createOrder.mutation";
import { StorageModule } from "@vlinder-web/storage-module-react";
import { REGISTER_PAYMENT } from "../graphql/registerPayment.mutation";
import { REGISTER_FISERV_PAYMENT } from "../graphql/registerFiservPayment.mutation";
import { handleFormPaymentResponse } from "../../../utils/formPayment.util";

const is = require("is_js");
export interface ICreateOrderArgs {
  email: string;
  name: string;
  payOrder: string;
  price: any;
  currency: string;
}

const getGraphQLModule = () => {
  const app = VApplication.getInstance();
  return app.getSync("services.GraphqlServerModule") as GraphqlServerModule;
};

export interface ICreateOrderTypeMethods extends IPluginMethodMap {
  createOrder(args: any): Promise<any>;
}

export class CreateOrderService implements IAgentPlugin {
  readonly methods: ICreateOrderTypeMethods;

  constructor() {
    this.methods = {
      createOrder: this.createOrder.bind(this),
    };
  }

  async createOrder(args: ICreateOrderArgs) {
    const graphqlModule = getGraphQLModule();
    const client = graphqlModule.getClient("graphql-gateway");
    let variable: any = {
      data: {
        name: args?.name,
        email: args?.email,
        payOrder: args?.payOrder,
        price: args?.price,
        currency: args?.currency,
      },
    };
    if (args?.code) {
      variable = { ...variable, discountCode: args?.code };
    }
    console.log(variable, "========>");
    const storageService = StorageModule.getInstance();

    try {
      if (client) {
        console.log(
          storageService.get("currencyExchangeLocal", "local")?.provider
        );
        const provider = storageService.get(
          "currencyExchangeLocal",
          "local"
        )?.provider;

        if (provider === "FISERV") {
          const gatewayCurrency =
            storageService.get("currencyExchangeLocal", "local")?.currency ||
            "TTD";
          const conversionRate =
            storageService.get("currencyExchangeLocal", "local")?.conversion_rate || 1;
          const rawPrice = args?.price ? parseFloat(args?.price as any) : 0;
          const userCurrency = args?.currency || "USD";

          let calculatedAmount = rawPrice;
          // if (userCurrency === "USD" && gatewayCurrency === "TTD") {
          //   calculatedAmount = Number((rawPrice * conversionRate).toFixed(2));
          // } else if (userCurrency === "TTD" && gatewayCurrency === "USD") {
          //   calculatedAmount = Number((rawPrice / conversionRate).toFixed(2));
          // }

          //fix: support ttd
          if (userCurrency === "USD") {
            calculatedAmount = Number((rawPrice / conversionRate).toFixed(2));
          }

          const costumeId =
            (args as any)?.costumeId || (args as any)?.costumeID || "";
          const costumeValueId =
            (args as any)?.costumeValueId || (args as any)?.costumeValue || "";

          const fiservPlatformFee =
            userCurrency === "USD" && args?.platformFee !== undefined
              ? args.platformFee > 10 && args.platformFee > (calculatedAmount * 0.1)
                ? Number((Number(args.platformFee) / conversionRate).toFixed(2))
                : args.platformFee
              : args?.platformFee;

          const fiservVariables: any = {
            data: {
              amount: calculatedAmount,
              //fix: support ttd
              currency: userCurrency,
              // currency: "USD",
              enable3DS: true,
              splitMap: [
                {
                  payOrder: args?.payOrder,
                  costumeId: costumeId,
                  amount: calculatedAmount,
                  allocations:
                    ((args as any)?.allocations &&
                      (args as any)?.allocations?.length > 0
                      ? (args as any)?.allocations
                        .filter((alloc: any) => !alloc?.isPaid && (alloc?.remainingCost === undefined || alloc?.remainingCost > 0))
                        .map((alloc: any) => ({
                          costumeValueId: alloc?.costumeValueId || costumeValueId,
                          amount: alloc?.amount || rawPrice,
                        }))
                      : costumeValueId
                        ? [
                          {
                            costumeValueId: costumeValueId,
                            amount: rawPrice,
                          },
                        ]
                        : []
                    ).filter((alloc: any) => (alloc?.amount ?? 0) > 0),
                },
              ],
              ...(fiservPlatformFee !== undefined && { platformFee: fiservPlatformFee }),
            },
          };

          const resFiserv = await client.mutate({
            mutation: REGISTER_FISERV_PAYMENT,
            variables: fiservVariables,
            fetchPolicy: "no-cache",
          });

          const fiservPayRes = resFiserv?.data?.registerFiservPayment;
          if (fiservPayRes) {
            handleFormPaymentResponse(fiservPayRes);
            return {
              submitted: true,
              ...fiservPayRes,
            };
          }
        }

        if (
          provider === "CAL" ||
          provider === "CALKIDS"
        ) {
          console.log(args);
          const calPlatformFee =
            args?.currency === "USD" && args?.platformFee !== undefined
              ? args.platformFee > 10 && args.platformFee > (Number(args?.price || 0) * 0.1)
                ? Number((Number(args.platformFee) / conversionRate).toFixed(2))
                : args.platformFee
              : args?.platformFee;

          let variables: any = {
            data: {
              currency: args?.currency,
              amount: args?.price,
              payorder: args?.payOrder,
              ...(calPlatformFee !== undefined && { platformFee: calPlatformFee }),
            },
          };
          if (args?.code) {
            variables = {
              ...variables,
              data: {
                ...variables?.data,
                discountCode: args?.code,
              },
            };
          }
          console.log("variables", variables);
          const resCal = await client.mutate({
            mutation: REGISTER_PAYMENT,
            variables: variables,
            fetchPolicy: "no-cache",
          });

          if (is.existy(resCal?.data?.registerPayment && args?.price != 0)) {
            const paymentUrl =
              resCal?.data?.registerPayment?.data?.session[
              "payment-hosted-url"
              ];
            if (paymentUrl) {
              // Redirect to the payment URL
              window.location.href = paymentUrl;
              console.log(`Redirecting to: ${paymentUrl}`);
              return {
                ...resCal?.data?.registerPayment,
                redirection: true,
              };
            }
            return {
              ...resCal?.data?.registerPayment,
              redirection: false,
            };
          } else if (args?.code && args?.price == 0) {
            return true;
          } else {
            throw new Error("Some Error Occurred");
          }
        } else {
          let variable: any = {
            data: {
              name: args?.name,
              email: args?.email,
              payOrder: args?.payOrder,
              price: args?.price,
              currency: args?.currency,
            },
          };
          if (args?.code) {
            variable = { ...variable, discountCode: args?.code };
          }
          const res = await client.mutate({
            mutation: CREATE_ORDER,
            variables: variable,
            fetchPolicy: "no-cache",
          });
          const paymentRes = res?.data?.getAnglPayRedirectURL;
          if (is.existy(paymentRes?.redirectUrl)) {
            const output = paymentRes?.redirectUrl;
            window.location.href = output;

            return paymentRes;
          } else if (paymentRes?.htmlCode || paymentRes?.formAction) {
            handleFormPaymentResponse(paymentRes);
            return {
              submitted: true,
              ...paymentRes,
            };
          } else if (
            is.existy(paymentRes?.sessionId?.sessionId)
          ) {
            const output =
              res?.data?.getAnglPayRedirectURL?.sessionId?.sessionId;
            return {
              sessionId: output,
            };
          } else if (args?.code) {
            return true;
          } else {
            throw new Error("Some Error Occurred");
          }
        }
      }
    } catch (err: any) {
      let _display_message =
        err?.graphQLErrors &&
        Array.isArray(err?.graphQLErrors) &&
        err?.graphQLErrors.find((item: any) => item.extensions);
      if (_display_message?.extensions?.display_message) {
        throw Error(_display_message?.extensions?.display_message);
      }

      throw err;
    }
  }
}
